import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import {
  Sparkles,
  UserRoundPen,
  Settings as SettingsIcon,
  Bell,
  LifeBuoy,
  MessageSquare,
  LogOut,
  MapPin,
  Eye,
  Pencil,
  ChevronRight,
} from "lucide-react";
import { useProfile } from "@/lib/api";
import { useAuth, usePlatform } from "@/lib/platform";
import { useCustomEvents, useShowReferralSection } from "@/lib/features";
import { useT } from "@/lib/i18n";
import { useAllQuizQuestions } from "@/data/quiz";
import { getAge } from "@/utils/dateUtils";
import { ProfileImage } from "@/components/ProfileImage";
import { PublicProfileModal } from "@/components/PublicProfileModal";
import { PageHeader } from "@/components/ux/PageHeader";
import { Loader } from "@/components/Loader";
import FeedbackDialog from "@/components/FeedbackDialog";
import EntitlementsSection from "../components/EntitlementsSection";
import ReferralSection from "../components/ReferralSection";
import { MyGroupsSection } from "../components/MyGroupsSection";
import { AccountRow } from "../components/AccountRow";

/** "a, b and c" */
const joinList = (items: string[]) =>
  items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

// The account tab. Leads with what a member comes here for: who they are to
// others, their tickets, invites and groups. Editing the public profile and
// the rarely-touched configuration (contact details, language, theme, legal,
// account deletion) each sit one tap away on their own screens.
export default function Profile() {
  const { t } = useT();
  const { signOut } = useAuth();
  const { isNative } = usePlatform();
  const showReferralSection = useShowReferralSection();
  const customEventsEnabled = useCustomEvents();
  const { data: profileData, isLoading } = useProfile({ staleTime: 0 });
  const allQuestions = useAllQuizQuestions();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const profile = profileData?.data?.profile as any;
  const user = profileData?.data?.user as any;
  const entitlements = profileData?.data?.entitlements || [];
  const referralId = profileData?.data?.user?.referralId;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  const pronounOptions = allQuestions.find((q) => q.id === "pronouns")?.options;
  const pronouns: string[] = (Array.isArray(profile?.pronouns) ? profile.pronouns : []).map(
    (v: string) => pronounOptions?.find((o) => o.value === v)?.label ?? v,
  );
  const age = getAge(profile?.birthday);
  const bio: string = profile?.bio?.trim() ?? "";

  // What's still empty on the public profile, in the order we'd ask for it.
  const missing = [
    !user?.profileImageUrl && "a photo",
    !bio && "a short bio",
    !profile?.interestTags?.length && "a few interests",
  ].filter(Boolean) as string[];

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-2xl mx-auto">
        <PageHeader
          title="Profile"
          hideBack
          action={
            <Link
              href="/profile/settings"
              aria-label="Settings"
              className="btn btn-ghost !px-2 inline-flex items-center justify-center"
            >
              <SettingsIcon className="w-6 h-6" />
            </Link>
          }
        />

        <div className="space-y-6">
          {profile ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card"
            >
              {/* Identity — a read-only summary; editing lives on /profile/edit */}
              <div className="flex items-center gap-4">
                <ProfileImage
                  size="xl"
                  className="flex-shrink-0"
                  user={{
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    profileImageUrl: user?.profileImageUrl,
                  }}
                />
                <div className="min-w-0 flex-1">
                  <h2 className="text-2xl font-bold dark:text-neutral-100 truncate">
                    {user?.firstName} {user?.lastName}
                    {age != null && (
                      <span className="font-normal text-neutral-500 dark:text-neutral-400">
                        , {age}
                      </span>
                    )}
                  </h2>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-neutral-600 dark:text-neutral-300">
                    {pronouns.length > 0 && <span>{pronouns.join(" / ")}</span>}
                    {user?.cityName && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {user.cityName}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {bio && (
                <p className="mt-4 text-sm text-neutral-700 dark:text-neutral-300 line-clamp-3">
                  {bio}
                </p>
              )}

              {missing.length > 0 && (
                <Link
                  href="/profile/edit"
                  className="group mt-4 flex items-center gap-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 p-3"
                >
                  <Sparkles className="w-5 h-5 text-primary-500 flex-shrink-0" />
                  <span className="min-w-0 flex-1 text-sm dark:text-neutral-200">
                    Add {joinList(missing)} so your table knows who's coming.
                  </span>
                  <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-primary-500 transition-colors flex-shrink-0" />
                </Link>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Link
                  href="/profile/edit"
                  className="btn btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Pencil className="w-4 h-4" />
                  Edit profile
                </Link>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="btn btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card text-center py-10"
            >
              <Sparkles className="w-14 h-14 text-primary-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-3 dark:text-neutral-100">
                {t("dashboard.completeProfileCard.title")}
              </h2>
              <p className="text-dark-muted dark:text-neutral-400 mb-6 max-w-md mx-auto">
                {t("dashboard.completeProfileCard.description")}
              </p>
              <Link
                href="/quiz"
                className="btn btn-primary inline-flex items-center justify-center"
              >
                {t("common.buttons.startQuiz")}
                <Sparkles className="ml-2 w-5 h-5 inline-block" />
              </Link>
            </motion.div>
          )}

          {/* Tickets / subscriptions */}
          <EntitlementsSection entitlements={entitlements} />

          {/* Invite friends */}
          {showReferralSection && referralId && !isNative && (
            <ReferralSection referralId={referralId} />
          )}

          {/* My Groups — feature flagged */}
          {customEventsEnabled && <MyGroupsSection />}

          <div className="space-y-2">
            {profile && (
              <AccountRow
                icon={UserRoundPen}
                title="Connection Profile"
                subtitle="How we match you to people, kikis & events"
                href="/profile/personality"
              />
            )}
            <AccountRow
              icon={Bell}
              title="Notifications"
              subtitle="Emails & push notifications"
              href="/profile/notifications"
            />
            <AccountRow
              icon={SettingsIcon}
              title="Settings"
              subtitle="Contact details, language, theme & legal"
              href="/profile/settings"
            />
          </div>

          <div className="space-y-2">
            <AccountRow
              icon={LifeBuoy}
              title="Help Center"
              subtitle="Answers to common questions"
              href="/help"
            />
            <AccountRow
              icon={MessageSquare}
              title={t("profile.feedback.giveFeedback")}
              subtitle="Tell us what to improve"
              onClick={() => setFeedbackOpen(true)}
            />
          </div>

          <AccountRow
            icon={LogOut}
            title={t("profile.account.signOut")}
            onClick={() => signOut()}
          />
        </div>
      </div>

      <PublicProfileModal
        userId={user?.id ?? null}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fresh
      />
      <FeedbackDialog isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}
